import React, { useState, useEffect } from 'react';
import { portfolioAPI, getImageUrl, portfolioCategoriesAPI } from '../../services/apiService';
import { Plus, Edit, Trash2, Eye, Star, Filter } from 'lucide-react';
import PortfolioCategoriesTab from '../../components/admin/PortfolioCategoriesTab';

const AdminPortfolio = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [activeTab, setActiveTab] = useState('projects');
  const [formData, setFormData] = useState({
    title_de: '',
    title_ru: '',
    description_de: '',
    description_ru: '',
    category: '',
    featured: false,
    tags: []
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchProjects();
    loadCategories();
  }, [filterCategory]);

  const loadCategories = async () => {
    try {
      // Load categories from public API
      const response = await fetch(`${window.location.origin}/api/portfolio-categories`);
      if (response.ok) {
        const data = await response.json();
        const allCategories = [
          { value: 'all', label: 'Все категории' },
          ...data.categories.map(cat => ({
            value: cat.slug || cat.id,
            label: cat.name_ru || cat.name_de
          }))
        ];
        setCategories(allCategories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback categories
      setCategories([
        { value: 'all', label: 'Все категории' },
        { value: 'wedding', label: 'Свадебная' },
        { value: 'portrait', label: 'Портретная' },
        { value: 'architecture', label: 'Архитектурная' },
        { value: 'events', label: 'События' },
        { value: 'restoration', label: 'Реставрация' },
        { value: 'ai-photoshoot', label: 'ИИ-Фотосессия' }
      ]);
    }
  };

  const fetchProjects = async () => {
    try {
      const params = filterCategory !== 'all' ? { category: filterCategory } : {};
      const response = await portfolioAPI.getAll(params);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = () => {
    setEditingProject(null);
    setFormData({
      title_de: '',
      title_ru: '',
      description_de: '',
      description_ru: '',
      category: '',
      featured: false,
      tags: []
    });
    setSelectedImage(null);
    setShowForm(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setFormData({
      title_de: project.title_de,
      title_ru: project.title_ru,
      description_de: project.description_de,
      description_ru: project.description_ru,
      category: project.category,
      featured: project.featured,
      tags: project.tags || []
    });
    setSelectedImage(null);
    setShowForm(true);
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот проект?')) {
      try {
        await portfolioAPI.delete(projectId);
        await fetchProjects();
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('Ошибка при удалении проекта');
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Add text fields
      Object.keys(formData).forEach(key => {
        if (key === 'tags') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add images if selected
      if (selectedImage) {
        if (Array.isArray(selectedImage)) {
          // Multiple images
          selectedImage.forEach((image, index) => {
            formDataToSend.append('images', image);
          });
        } else {
          // Single image (backward compatibility)
          formDataToSend.append('image', selectedImage);
        }
      }

      if (editingProject) {
        await portfolioAPI.update(editingProject.id, formDataToSend);
      } else {
        await portfolioAPI.create(formDataToSend);
      }

      setShowForm(false);
      await fetchProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleSubmit = (e) => {
    handleFormSubmit(e);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProject(null);
    setFormData({
      title_de: '',
      title_ru: '',
      description_de: '',
      description_ru: '',
      category: '',
      featured: false,
      tags: []
    });
    setSelectedImage(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление портфолио</h1>
          <p className="text-gray-300">Управление проектами и категориями портфолио</p>
        </div>
        {activeTab === 'projects' && (
          <button
            onClick={handleCreateProject}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors duration-200"
          >
            <Plus className="h-5 w-5 mr-2" />
            Добавить проект
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg">
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-6 py-3 text-sm font-medium rounded-tl-lg ${
              activeTab === 'projects'
                ? 'bg-gray-700 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Проекты
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-3 text-sm font-medium ${
              activeTab === 'categories'
                ? 'bg-gray-700 text-white border-b-2 border-blue-500'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Категории
          </button>
        </div>
        <div className="p-6">
          {activeTab === 'projects' ? (
            <ProjectsTab 
              projects={projects}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              showForm={showForm}
              setShowForm={setShowForm}
              editingProject={editingProject}
              setEditingProject={setEditingProject}
              formData={formData}
              setFormData={setFormData}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
              isSubmitting={isSubmitting}
              categories={categories}
              handleCreateProject={handleCreateProject}
              handleEditProject={handleEditProject}
              handleSubmit={handleSubmit}
              handleDeleteProject={handleDeleteProject}
              resetForm={resetForm}
            />
          ) : (
            <PortfolioCategoriesTab onCategoriesUpdate={loadCategories} />
          )}
        </div>
      </div>
    </div>
  );
};

// Projects Tab Component
const ProjectsTab = ({ 
  projects, 
  filterCategory, 
  setFilterCategory, 
  showForm, 
  setShowForm, 
  editingProject, 
  setEditingProject,
  formData, 
  setFormData, 
  selectedImage, 
  setSelectedImage, 
  isSubmitting, 
  categories,
  handleCreateProject,
  handleEditProject,
  handleSubmit,
  handleDeleteProject,
  resetForm
}) => {
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-700">
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {categories.map(category => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
          <div className="text-sm text-gray-300">
            {projects.length} project{projects.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="bg-gray-800 rounded-lg shadow-lg overflow-hidden border border-gray-700">
            <div className="aspect-w-16 aspect-h-9 bg-gray-900">
              <img
                src={getImageUrl(project.thumbnail)}
                alt={project.title_de}
                className="w-full h-48 object-cover"
              />
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-white truncate">
                  {project.title_de}
                </h3>
                {project.featured && (
                  <Star className="h-5 w-5 text-yellow-400 fill-current" />
                )}
              </div>
              <p className="text-gray-300 text-sm mb-3 line-clamp-2">
                {project.description_de}
              </p>
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-900 text-indigo-200">
                  {categories.find(cat => cat.value === project.category)?.label || project.category}
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditProject(project)}
                    className="text-blue-400 hover:text-blue-300 p-1"
                    title="Редактировать"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteProject(project.id)}
                    className="text-red-400 hover:text-red-300 p-1"
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">
                  {editingProject ? 'Редактировать проект' : 'Новый проект'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-white"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Заголовок (DE)
                  </label>
                  <input
                    type="text"
                    value={formData.title_de}
                    onChange={(e) => setFormData({...formData, title_de: e.target.value})}
                    className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Заголовок (RU)
                  </label>
                  <input
                    type="text"
                    value={formData.title_ru}
                    onChange={(e) => setFormData({...formData, title_ru: e.target.value})}
                    className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Описание (DE)
                  </label>
                  <textarea
                    value={formData.description_de}
                    onChange={(e) => setFormData({...formData, description_de: e.target.value})}
                    rows={3}
                    className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Описание (RU)
                  </label>
                  <textarea
                    value={formData.description_ru}
                    onChange={(e) => setFormData({...formData, description_ru: e.target.value})}
                    rows={3}
                    className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Категория
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full border border-gray-600 bg-gray-700 text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Выберите категорию</option>
                    {categories.filter(cat => cat.value !== 'all').map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Featured */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 border-gray-600 bg-gray-700 rounded focus:ring-indigo-500"
                  />
                  <label htmlFor="featured" className="ml-2 text-sm text-gray-300">
                    Рекомендуемый проект
                  </label>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Изображения (можно выбрать несколько)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setSelectedImage(Array.from(e.target.files))}
                    className="w-full text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-600 file:text-white hover:file:bg-indigo-700"
                    required={!editingProject}
                  />
                  {selectedImage && Array.isArray(selectedImage) && selectedImage.length > 0 && (
                    <div className="mt-2 text-sm text-gray-400">
                      Выбрано изображений: {selectedImage.length}
                    </div>
                  )}
                </div>

                {/* Submit */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-700"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Сохранение...' : editingProject ? 'Обновить' : 'Создать'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPortfolio;